import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";

const CONFIG = {
  good: {
    bg: "bg-emerald-50",
    border: "border-emerald-300",
    text: "text-emerald-800",
    accent: "text-emerald-600",
    Icon: CheckCircle2,
    label: "ALL CLEAR",
  },
  attention: {
    bg: "bg-amber-50",
    border: "border-amber-300",
    text: "text-amber-800",
    accent: "text-amber-600",
    Icon: AlertTriangle,
    label: "ATTENTION",
  },
  risk: {
    bg: "bg-red-50",
    border: "border-red-300",
    text: "text-red-800",
    accent: "text-red-600",
    Icon: ShieldAlert,
    label: "GROUNDING RISK",
  },
};

export default function CarrierHealthBanner() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/health");
        if (alive) setHealth(data);
      } catch {
        // silent — banner just doesn't render
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="w-full p-6 md:p-8 border-2 border-slate-200 bg-slate-50 rounded-lg" data-testid="carrier-health-loading">
        <div className="hs-overline text-slate-400 animate-pulse">CHECKING CARRIER HEALTH…</div>
      </div>
    );
  }
  if (!health) return null;

  const cfg = CONFIG[health.level] || CONFIG.good;
  const { Icon } = cfg;

  return (
    <div
      className={`w-full p-6 md:p-8 rounded-lg border-2 ${cfg.bg} ${cfg.border} flex flex-col md:flex-row items-start md:items-center justify-between gap-5`}
      data-testid={`carrier-health-banner-${health.level}`}
    >
      <div className="flex items-start gap-4 min-w-0 flex-1">
        <div className={`flex-shrink-0 ${cfg.accent} p-2 bg-white rounded-md border ${cfg.border}`}>
          <Icon size={32} strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className={`hs-overline ${cfg.accent} font-bold`}>{cfg.label}</div>
          <div className={`font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-1 ${cfg.text}`} data-testid="carrier-health-headline">
            {health.headline}
          </div>
          {health.reasons?.length > 0 && (
            <ul className="mt-3 space-y-1" data-testid="carrier-health-reasons">
              {health.reasons.map((r, i) => (
                <li key={i} className={`text-sm ${cfg.text} opacity-90 flex items-center gap-2`}>
                  <span className={`w-1 h-1 rounded-full ${cfg.accent} bg-current flex-shrink-0`} /> {r}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 md:gap-4 w-full md:w-auto flex-shrink-0">
        <Mini value={health.counts.overdue_filings} label="OVERDUE" danger={health.counts.overdue_filings > 0} testid="ch-overdue" />
        <Mini value={health.counts.expiring_30d} label="DUE 30d" warn={health.counts.expiring_30d > 0} testid="ch-due30" />
        <Mini value={health.counts.open_violations} label="OPEN TICKETS" warn={health.counts.open_violations > 0} testid="ch-open" />
      </div>
    </div>
  );
}

function Mini({ value, label, danger, warn, testid }) {
  const color = danger ? "text-red-600" : warn ? "text-amber-700" : "text-slate-900";
  return (
    <div className="text-center min-w-[72px]" data-testid={testid}>
      <div className={`font-display font-extrabold text-3xl md:text-4xl tracking-tighter ${color}`}>{value}</div>
      <div className="hs-overline text-[10px] mt-1 text-slate-500">{label}</div>
    </div>
  );
}
